import {useCallback, useEffect, useState} from "react";
import {AlertTriangle, Clock, FileSpreadsheet} from "lucide-react";
import {
  adminLogExportUrl,
  banAdminIp,
  getAdminAccessRestriction,
  getAdminLog,
  listAdminLogs,
  unbanAdminIp,
  updateAdminAccessRestriction,
  type AdminLogRow
} from "@zaaaz/shared";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Textarea} from "@/components/ui/textarea";
import {AdminAlert, AdminBadge, AdminCard, AdminEditModal, AdminField, AdminFormActions, AdminModernTable, AdminPageLayout, AdminPagination, AdminToolbar} from "./ui";

export function LogView() {
  const [items, setItems] = useState<AdminLogRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [failedAuth, setFailedAuth] = useState(0);
  const [blockedIps, setBlockedIps] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AdminLogRow | null>(null);
  const [ipsText, setIpsText] = useState("");
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminLogs({page, pageSize: 20, search: searchQuery || undefined}).then((r) => {
      setItems(r.items);
      setTotalPages(r.totalPages);
      setFailedAuth(r.failedAuthCount);
      setBlockedIps(r.blockedIps);
    }).catch((e: Error) => setErr(e.message));
    getAdminAccessRestriction().then((r) => setIpsText(r.ips)).catch(() => {});
  }, [page, searchQuery]);
  useEffect(() => { load(); }, [load]);

  const commitSearch = useCallback(() => {
    setSearchQuery(search);
    setPage(1);
  }, [search]);

  async function openLog(id: number) {
    setOpenId(id);
    setModalOpen(true);
    setDetail(await getAdminLog(id));
  }
  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) { setOpenId(null); setDetail(null); }
  }
  async function saveRestriction() {
    try {
        await updateAdminAccessRestriction(ipsText);
      load();
      adminToast.success("IP list updated.");
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Save failed");
    }
  }
  async function toggleIpBan(ip: string, blocked: boolean) {
    if(blocked) await unbanAdminIp(ip);
    else await banAdminIp(ip);
    load();
    if(openId) setDetail(await getAdminLog(openId));
  }
  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title="Access log"
      description="Admin panel access records"
      breadcrumbs={[{label: "Dashboard", href: "/vadmin"}, {label: "Access log"}]}
    >
      <AdminToolbar>
        <Button variant="secondary" size="sm" asChild>
          <a href={adminLogExportUrl()} target="_blank" rel="noreferrer"><FileSpreadsheet className="h-4 w-4" /> Export records</a>
        </Button>
        {failedAuth > 0 ? <AdminBadge variant="danger"><AlertTriangle className="h-3 w-3" /> Autentificari nereusite <b>{failedAuth}</b></AdminBadge> : null}
      </AdminToolbar>
      <AdminCard title="Admin access restriction (blocked IPs)">
        <AdminField label="IPs separated by commas">
          <Textarea rows={3} value={ipsText} onChange={(e) => setIpsText(e.target.value)} placeholder="IPs separated by commas" />
        </AdminField>
        <AdminFormActions>
          <Button type="button" size="sm" onClick={() => saveRestriction()}>Save IP list</Button>
        </AdminFormActions>
        {blockedIps.length > 0 ? <p className="mt-3 text-sm text-muted-foreground">Blocked: {blockedIps.join(", ")}</p> : null}
      </AdminCard>
      <AdminModernTable
        items={items}
        getId={(l) => l.id}
        getTitle={(l) => l.email}
        getSubtitle={(l) => l.ip}
        getMeta={(l) => (
          <>
            {l.status === 0 ? <AdminBadge variant="danger"><AlertTriangle className="h-3 w-3" /></AdminBadge> : null}
            {l.sessionMinutes > 0 ? <AdminBadge variant="secondary"><Clock className="h-3 w-3" /> {l.sessionMinutes} min</AdminBadge> : null}
            <AdminBadge variant="secondary">{l.dateLabel}</AdminBadge>
            <AdminBadge variant="outline">{l.statusLabel}</AdminBadge>
          </>
        )}
        getSearchText={(l) => adminSearchText(l.id, l.email, l.ip, l.browser, l.pages, l.statusLabel, l.dateLabel, l.sessionMinutes)}
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={commitSearch}
        clientFilter={false}
        selectedId={openId}
        onRowClick={(l) => openLog(l.id)}
        emptyTitle="No records"
        emptyDescription="Adjust search or check back later."
        searchPlaceholder="Search email, IP, browser, pagini..."
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={detail ? `Log #${detail.id}` : "Log details"}
        icon={Clock}
      >
        {detail ? (
          <>
            <p><b>Status:</b> {detail.statusLabel}</p>
            <p>
              <b>IP:</b> {detail.ip}{" "}
              {detail.ip ? (
                <Button type="button" variant="outline" size="sm" className="admin-detail-panel__inline-btn" onClick={() => toggleIpBan(detail.ip, !!detail.ipBlocked)}>
                  {detail.ipBlocked ? "Restore access" : "Restrict access"}
                </Button>
              ) : null}
            </p>
            <p><b>Browser:</b> {detail.browser}</p>
            {detail.pages ? <pre className="admin-detail-panel__pre">{detail.pages}</pre> : null}
          </>
        ) : null}
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
